import LoadMoreButton from '@rio-cloud/rio-uikit/LoadMoreButton';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import type { SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import { isEmpty } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { Driver, DriverStatus, Tag, User } from '../../../../../../types';
import { Empty } from '../../../Empty';
import { ErrorBlock } from '../../../ErrorBlock';
import { FilteredEmpty } from '../../../FilteredEmpty';
import { Loading } from '../../../Loading';
import { NotFound } from '../../../NotFound';
import { DriversTable } from '../../components/DriversTable';
import { DriversTableToolbar } from '../../components/DriversTableToolbar';
import {
    areDriverTableColumnsDefault,
    buildDefaultDriverTableColumns,
    getDriverTableStorageKey,
    mergeDriverTableColumns,
    normalizeDriverTableColumnsForStorage,
    restoreDriverTableColumnOrder,
    type DriverTableColumnConfig,
    type DriverTablePersistedColumnConfig,
} from '../../components/driverTableColumns';
import { formatUserNameForDisplay } from '../../../../utils';
import { driverCardOrLicenseIntlMessage } from '../../../../tenantTypes';
import type { TablePropertiesFromDispatch, TablePropertiesFromState } from '../containers/DriverTableWrapper';

const WRAPPER_ID = 'DriverTable';
export const DRIVERS_PER_PAGE = 100;

type TableProperties = TablePropertiesFromState & TablePropertiesFromDispatch;

export const Table = (props: TableProperties) => {
    const intl = useIntl();
    const {
        accountId,
        allTagsInAccount,
        driverStatuses,
        drivers,
        driversListMode,
        fetchDriverRequested,
        hasFetchedDriversOnce,
        hasDataFetchError,
        searchText,
        selectedDriver,
        selectedDriverId,
        selectionChange,
        sortBy,
        sortDir,
        tenant,
        users,
        viewTypeChange,
    } = props;
    const [selectedDriverIdsForMultiSelect, setSelectedDriverIdsForMultiSelect] = useState<string[]>([]);
    const [numberOfDriversToDisplay, setNumberOfDriversToDisplay] = useState(DRIVERS_PER_PAGE);
    const filteredDrivers = useMemo(
        () => filterDrivers(drivers, users, searchText, driverStatuses, allTagsInAccount),
        [allTagsInAccount, driverStatuses, drivers, searchText, users]
    );
    const [gridLines, setGridLines] = useState(false);
    const [compactRows, setCompactRows] = useState(false);
    const [tableResetKey, setTableResetKey] = useState(0);

    const defaultColumns = useMemo(
        () =>
            buildDefaultDriverTableColumns({
                card: driverCardOrLicenseIntlMessage(tenant, intl),
                driver: intl.formatMessage({ id: 'intl-msg:driver', defaultMessage: 'Driver' }),
                email: intl.formatMessage({ id: 'intl-msg:email' }),
                firstName: intl.formatMessage({ id: 'intl-msg:firstName' }),
                lastName: intl.formatMessage({ id: 'intl-msg:lastName' }),
                phone: intl.formatMessage({ id: 'intl-msg:phoneNumber' }),
                status: intl.formatMessage({ id: 'intl-msg:status' }),
                tag: intl.formatMessage({ id: 'intl-msg:tag' }),
                user: intl.formatMessage({ id: 'intl-msg:user' }),
            }),
        [intl, tenant]
    );
    const storageKey = useMemo(() => getDriverTableStorageKey(accountId), [accountId]);
    const [persistedColumns, setPersistedColumns, removePersistedColumns] = useLocalStorage<
        DriverTablePersistedColumnConfig[]
    >(storageKey, []);

    const [columns, setColumns] = useState<DriverTableColumnConfig[]>(() =>
        mergeDriverTableColumns(defaultColumns, persistedColumns)
    );

    useEffect(() => {
        if (selectedDriverId && !fetchDriverRequested && selectedDriver?.driverId !== selectedDriverId) {
            selectionChange(selectedDriverId);
        }
    }, [fetchDriverRequested, selectedDriver?.driverId, selectedDriverId, selectionChange]);

    useEffect(() => {
        setNumberOfDriversToDisplay(DRIVERS_PER_PAGE);
    }, [driverStatuses, drivers, searchText]);

    useEffect(() => {
        const availableDriverIds = new Set(drivers.map(driver => driver.driverId));
        const nextSelectedDriverIds = selectedDriverIdsForMultiSelect.filter(driverId =>
            availableDriverIds.has(driverId)
        );

        if (nextSelectedDriverIds.length !== selectedDriverIdsForMultiSelect.length) {
            setSelectedDriverIdsForMultiSelect(nextSelectedDriverIds);
        }
    }, [drivers, selectedDriverIdsForMultiSelect]);

    useEffect(() => {
        const mergedColumns = mergeDriverTableColumns(defaultColumns, persistedColumns);
        setColumns(previousColumns => {
            const currentColumns = JSON.stringify(normalizeDriverTableColumnsForStorage(previousColumns));
            const nextColumns = JSON.stringify(normalizeDriverTableColumnsForStorage(mergedColumns));

            return currentColumns === nextColumns ? previousColumns : mergedColumns;
        });
    }, [defaultColumns, persistedColumns]);

    const persistColumns = (nextColumns: DriverTableColumnConfig[]) => {
        if (areDriverTableColumnsDefault(nextColumns, defaultColumns)) {
            removePersistedColumns();
            return;
        }

        setPersistedColumns(normalizeDriverTableColumnsForStorage(nextColumns));
    };

    const toggleAllCheckboxes = (enable: boolean) => {
        if (enable) {
            const filteredDriverIds = filteredDrivers.map(driver => driver.driverId);
            const hiddenSelectedDriverIds = selectedDriverIdsForMultiSelect.filter(
                driverId => !filteredDriverIds.includes(driverId)
            );
            setSelectedDriverIdsForMultiSelect(filteredDriverIds.concat(hiddenSelectedDriverIds));
            return;
        }

        setSelectedDriverIdsForMultiSelect(
            selectedDriverIdsForMultiSelect.filter(
                driverId => !filteredDrivers.some(filteredDriver => filteredDriver.driverId === driverId)
            )
        );
    };

    const handleSelectionChange = (rowIds: string[]) => {
        setSelectedDriverIdsForMultiSelect(rowIds);
    };

    const handleRowSelectionChange = (rowId: string | undefined) => {
        if (fetchDriverRequested) {
            return;
        }

        if (rowId && rowId !== selectedDriver?.driverId) {
            selectionChange(rowId);
            return;
        }

        selectionChange(undefined);
    };

    const handleActiveRowChange = (rowId: string | undefined) => handleRowSelectionChange(rowId);

    const handleRowClick = (rowId: string) => {
        if (rowId === selectedDriver?.driverId) {
            handleRowSelectionChange(rowId);
        }
    };

    const handleSortChange = (columnKey: string, direction: SortDirectionType) => {
        props.sortChange(columnKey, direction);
    };

    const handleSearchTextChange = (nextSearchText: string) => {
        props.searchTextChanged(nextSearchText);
    };

    const handleLoadMore = () => {
        setNumberOfDriversToDisplay(previousValue => previousValue + DRIVERS_PER_PAGE);
    };

    const handleColumnOrderChange = (columnOrder: string[]) => {
        setColumns(previousColumns => {
            const columnsByKey = new Map(previousColumns.map(column => [column.key, column]));
            const defaultColumnOrder = new Map(defaultColumns.map((column, index) => [column.key, index]));
            const orderedColumns = columnOrder.flatMap(columnKey => {
                const column = columnsByKey.get(columnKey as DriverTableColumnConfig['key']);
                return column ? [column] : [];
            });
            const missingColumns = previousColumns
                .filter(column => !columnOrder.includes(column.key))
                .sort(
                    (left, right) =>
                        (defaultColumnOrder.get(left.key) ?? Number.MAX_SAFE_INTEGER) -
                        (defaultColumnOrder.get(right.key) ?? Number.MAX_SAFE_INTEGER)
                );
            const nextColumns = orderedColumns.concat(missingColumns);

            persistColumns(nextColumns);
            return nextColumns;
        });
    };

    const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
        setColumns(previousColumns => {
            const updatedColumns = previousColumns.map(column =>
                column.key === columnKey ? { ...column, visible } : column
            );
            const nextColumns = visible
                ? restoreDriverTableColumnOrder(
                      updatedColumns,
                      columnKey as DriverTableColumnConfig['key'],
                      defaultColumns
                  )
                : updatedColumns;

            persistColumns(nextColumns);
            return nextColumns;
        });
    };

    const handleColumnResize = (columnKey: string, width?: number | string) => {
        setColumns(previousColumns => {
            const nextColumns = previousColumns.map(column =>
                column.key === columnKey
                    ? { ...column, width: width ?? defaultColumns.find(item => item.key === columnKey)?.width }
                    : column
            );

            persistColumns(nextColumns);
            return nextColumns;
        });
    };

    const handleResetColumns = () => {
        removePersistedColumns();
        setColumns(defaultColumns.map(column => ({ ...column })));
        setTableResetKey(previousValue => previousValue + 1);
    };

    const hasData = !isEmpty(drivers);
    const driversToRender = filteredDrivers.slice(0, numberOfDriversToDisplay);
    const hasEmptySearchResult = searchText && isEmpty(driversToRender);
    const showCreateButtonInToolbar = filteredDrivers.length > 0;
    const handleCreateDriver = () => props.toggleDriverCreationDialog(true);

    const getContent = () => {
        if (hasDataFetchError) {
            return <ErrorBlock />;
        }

        if (!hasFetchedDriversOnce) {
            return <Loading />;
        }

        if (hasEmptySearchResult) {
            return <NotFound onCreateDriver={handleCreateDriver} />;
        }

        if (!hasData && !searchText) {
            return <Empty onCreateDriver={handleCreateDriver} />;
        }

        if (isEmpty(filteredDrivers)) {
            return <FilteredEmpty onCreateDriver={handleCreateDriver} />;
        }

        return (
            <>
                <DriversTable
                    columns={columns}
                    compactRows={compactRows}
                    drivers={driversToRender}
                    driversListMode={driversListMode}
                    gridLines={gridLines}
                    searchText={searchText}
                    onActiveRowChange={handleActiveRowChange}
                    onRowClick={handleRowClick}
                    onColumnOrderChange={handleColumnOrderChange}
                    onColumnResize={handleColumnResize}
                    onSelectionChange={handleSelectionChange}
                    onSortChange={handleSortChange}
                    onViewTypeChange={viewTypeChange}
                    selectedDriverId={selectedDriverId}
                    selectedDriverIdsForMultiSelect={selectedDriverIdsForMultiSelect}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    tableResetKey={tableResetKey}
                    tenant={tenant}
                    toggleAllCheckboxes={toggleAllCheckboxes}
                    users={users}
                />
                <LoadMoreButton
                    data-testid='loadMoreButton'
                    loaded={driversToRender.length}
                    total={filteredDrivers.length}
                    onLoadMore={handleLoadMore}
                    loadMoreMessage={<FormattedMessage id='intl-msg:drivers.loadMore' />}
                    noMoreMessage={<FormattedMessage id='intl-msg:drivers.noMore' />}
                />
            </>
        );
    };

    return (
        <div data-semantic-id='drivers-view' id={WRAPPER_ID}>
            <DriversTableToolbar
                columns={columns}
                compactRows={compactRows}
                driversListMode={driversListMode}
                gridLines={gridLines}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onResetColumns={handleResetColumns}
                onSearchTextChange={handleSearchTextChange}
                onToggleCompactRows={() => setCompactRows(previousValue => !previousValue)}
                onToggleGridLines={() => setGridLines(previousValue => !previousValue)}
                onViewTypeChange={viewTypeChange}
                searchText={searchText}
                toggleDriverCreationDialog={props.toggleDriverCreationDialog}
                showCreateButton={showCreateButtonInToolbar}
            />
            {getContent()}
        </div>
    );
};

export const filterDrivers = (
    drivers: Driver[],
    users: User[],
    searchText: string,
    driverStatuses: DriverStatus[],
    allTagsInAccount: Tag[]
) => {
    return searchText
        .toLocaleLowerCase()
        .split(' ')
        .reduce(
            (previousResult, searchString) =>
                previousResult.filter(driver => {
                    const user = users.find(userEntry => userEntry.subject === driver.subject);
                    return (
                        driverStatuses.includes(driver.status) &&
                        matchesSearch(driver, user, searchString, allTagsInAccount)
                    );
                }),
            drivers
        );
};

const matchesSearch = (driver: Driver, user: User | undefined, searchString: string, allTagsInAccount: Tag[]) => {
    return (
        driver.firstName?.toLowerCase().includes(searchString) ||
        driver.lastName?.toLowerCase().includes(searchString) ||
        driver.activeCardNumber?.toLowerCase().includes(searchString) ||
        driver.email?.toLowerCase().includes(searchString) ||
        driver.phoneNumber?.toLowerCase().includes(searchString) ||
        formatUserNameForDisplay(user).toLowerCase().includes(searchString) ||
        driver.tags?.some(tagId =>
            allTagsInAccount.some(tag => tag.id === tagId && tag.name.toLowerCase().includes(searchString))
        )
    );
};
